/**
 * batch-69 planbord-afspraak (FO-523, spec 223) - pure regels voor een afspraak
 * met een klant. Geen I/O: de API (routes, cron, board-loader), de backoffice-
 * dialoog en de portal rekenen met dezelfde functies, zodat scherm, melding en
 * server nooit iets anders vinden.
 *
 * Tijd: een afspraak staat als NL-kalenderdag (`yyyy-mm-dd`) plus "HH:mm" in
 * Europe/Amsterdam opgeslagen (zelfde conventie als `WerkbonDayEntry`). Voor de
 * cron (een kwartier na de eindtijd, 4 uur, de volgende ochtend, 48 uur) rekenen
 * we naar echte UTC-instanten via `amsterdamWallclockToUtc` (DST-correct).
 */

import type {
  AfspraakVerslagStatus,
  CrmAfspraakInstellingen,
  ContactMomentZichtbaarheid,
  PlanningAfspraakLocatieSoort,
  PlanningAfspraakReden,
  PlanningAfspraakStatus,
  PlanningConflictDTO,
} from '@streep/types'
import { PLANNING_AFSPRAAK_LOCATIE_LABELS, PLANNING_AFSPRAAK_REDEN_LABELS } from '@streep/types'
import { amsterdamWallclockToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'

// ─── Contactmoment ───────────────────────────────────────────────────────────

/** Het CRM-type van het contactmoment, afgeleid uit waar het gesprek is. */
export function contactMomentTypeVoorLocatie(
  soort: PlanningAfspraakLocatieSoort,
): 'BEZOEK' | 'TEAMS' | 'TELEFOON' {
  if (soort === 'TEAMS') return 'TEAMS'
  if (soort === 'TELEFOON') return 'TELEFOON'
  return 'BEZOEK'
}

/** Het onderwerp van het contactmoment: de reden, met de korte vrije tekst erachter. */
export function afspraakOnderwerp(reden: PlanningAfspraakReden, toelichting?: string | null): string {
  const label = PLANNING_AFSPRAAK_REDEN_LABELS[reden]
  const tekst = toelichting?.trim()
  return (tekst ? `${label}: ${tekst}` : label).slice(0, 250)
}

/** Kort locatielabel voor een blok of een melding ("Teams", "Hoofdkantoor", ...). */
export function afspraakLocatieLabel(input: {
  locatieSoort: PlanningAfspraakLocatieSoort
  locatieNaam?: string | null
  locatieOmschrijving?: string | null
}): string {
  if (input.locatieSoort === 'KLANTLOCATIE' && input.locatieNaam) return input.locatieNaam
  if (input.locatieSoort === 'ANDERS' && input.locatieOmschrijving?.trim()) return input.locatieOmschrijving.trim()
  return PLANNING_AFSPRAAK_LOCATIE_LABELS[input.locatieSoort]
}

// ─── Tijd ────────────────────────────────────────────────────────────────────

/** UTC-middernacht van een NL-kalenderdag (de opslagvorm van `PlanningAfspraak.datum`). */
export function afspraakDatumOpslag(ymd: string): Date {
  return new Date(`${ymd}T00:00:00.000Z`)
}

/** De NL-kalenderdag (`yyyy-mm-dd`) van de opgeslagen datum. */
export function afspraakDatumSleutel(datum: Date): string {
  return datum.toISOString().slice(0, 10)
}

/** Begin en eind van een afspraak als echte UTC-instanten (DST-correct). */
export function afspraakInstanten(input: {
  datum: string | Date
  startTijd: string
  eindTijd: string
}): { start: Date; eind: Date } {
  const ymd = typeof input.datum === 'string' ? input.datum.slice(0, 10) : afspraakDatumSleutel(input.datum)
  return {
    start: amsterdamWallclockToUtc(ymd, input.startTijd),
    eind: amsterdamWallclockToUtc(ymd, input.eindTijd),
  }
}

/** Duur in minuten (eind ligt altijd na start op dezelfde dag). */
export function afspraakDuurMinuten(startTijd: string, eindTijd: string): number {
  const [sh, sm] = startTijd.split(':').map(Number)
  const [eh, em] = eindTijd.split(':').map(Number)
  return (eh ?? 0) * 60 + (em ?? 0) - ((sh ?? 0) * 60 + (sm ?? 0))
}

/** Verschuif een NL-kalenderdag met n dagen (kale datum, geen DST-sprong). */
function plusDagen(ymd: string, dagen: number): string {
  const d = new Date(`${ymd}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + dagen)
  return d.toISOString().slice(0, 10)
}

export interface VerslagMomenten {
  /** Push/melding "maak je verslag" (standaard een kwartier na de eindtijd). */
  vragenOp: Date
  /** Eerste herinnering (standaard 4 uur na de eindtijd). */
  herinnering1Op: Date
  /** Tweede herinnering: de ochtend NA de afspraakdag om het ingestelde uur (standaard 08:00 NL). */
  herinnering2Op: Date
  /** Signaal aan teamleider en accountmanager (standaard 48 uur na de eindtijd). */
  escalatieOp: Date
}

/** Stille uren (NL-tijd): geen herinnering "na een paar uur" in de nacht. */
export const AFSPRAAK_STILLE_UREN = { van: '22:00', tot: '07:00' } as const

/**
 * De eerste herinnering buiten de stille uren: valt hij erin, dan schuift hij naar
 * het einde ervan (07:00 NL). Komt de ochtendherinnering diezelfde ochtend, dan
 * vallen ze samen: een melding, de ochtendherinnering dekt het.
 */
function buitenStilleUren(moment: Date, ochtend: Date): Date {
  const { ymd, hm } = utcToAmsterdamWallclock(moment)
  let verschoven: Date
  if (hm >= AFSPRAAK_STILLE_UREN.van) verschoven = amsterdamWallclockToUtc(plusDagen(ymd, 1), AFSPRAAK_STILLE_UREN.tot)
  else if (hm < AFSPRAAK_STILLE_UREN.tot) verschoven = amsterdamWallclockToUtc(ymd, AFSPRAAK_STILLE_UREN.tot)
  else return moment
  return utcToAmsterdamWallclock(verschoven).ymd === utcToAmsterdamWallclock(ochtend).ymd ? ochtend : verschoven
}

/**
 * De momenten van de verslagplicht voor een afspraak. "De volgende ochtend" is de
 * NL-kalenderdag na de afspraak, op het ingestelde uur in NL-tijd: een afspraak
 * die om 16:00 eindigt krijgt zijn tweede herinnering de volgende dag om 08:00.
 * De herinnering na een paar uur valt nooit in de stille uren (zie
 * `AFSPRAAK_STILLE_UREN`): een afspraak die om 21:00 eindigt, krijgt geen
 * melding om 01:00 maar een om 08:00.
 */
export function bepaalVerslagMomenten(
  afspraak: { datum: string | Date; startTijd: string; eindTijd: string },
  instellingen: Pick<
    CrmAfspraakInstellingen,
    'verslagVraagNaMinuten' | 'herinneringNaUren' | 'ochtendHerinneringUur' | 'escalatieNaUren'
  >,
): VerslagMomenten {
  const { eind } = afspraakInstanten(afspraak)
  const ymd =
    typeof afspraak.datum === 'string' ? afspraak.datum.slice(0, 10) : afspraakDatumSleutel(afspraak.datum)
  const uur = String(instellingen.ochtendHerinneringUur).padStart(2, '0')
  const herinnering2Op = amsterdamWallclockToUtc(plusDagen(ymd, 1), `${uur}:00`)
  return {
    vragenOp: new Date(eind.getTime() + instellingen.verslagVraagNaMinuten * 60_000),
    herinnering1Op: buitenStilleUren(new Date(eind.getTime() + instellingen.herinneringNaUren * 3_600_000), herinnering2Op),
    herinnering2Op,
    escalatieOp: new Date(eind.getTime() + instellingen.escalatieNaUren * 3_600_000),
  }
}

/**
 * Het moment van het 48-uurs-signaal, gerekend vanaf het moment dat het verslag
 * ECHT werd gevraagd. Normaal is dat een kwartier na de eindtijd (dan is het
 * gewoon `escalatieOp`). Is de afspraak achteraf vastgelegd of naar het verleden
 * verplaatst, dan kwam de vraag later: dan krijgen de deelnemers dezelfde termijn
 * vanaf die vraag, in plaats van een signaal aan teamleider en accountmanager op
 * het moment dat ze de vraag krijgen. Zonder vraag (`null`): `escalatieOp`.
 */
export function effectiefEscalatieMoment(
  momenten: Pick<VerslagMomenten, 'vragenOp' | 'escalatieOp'>,
  verslagGevraagdOp: Date | null,
): Date {
  if (!verslagGevraagdOp) return momenten.escalatieOp
  const termijn = momenten.escalatieOp.getTime() - momenten.vragenOp.getTime()
  return new Date(Math.max(momenten.escalatieOp.getTime(), verslagGevraagdOp.getTime() + termijn))
}

/** Het moment van de herinnering vooraf, of null zonder herinnering. */
export function herinneringVooraf(
  afspraak: { datum: string | Date; startTijd: string; eindTijd: string },
  minutenVooraf: number | null,
): Date | null {
  if (minutenVooraf === null || minutenVooraf <= 0) return null
  const { start } = afspraakInstanten(afspraak)
  return new Date(start.getTime() - minutenVooraf * 60_000)
}

/**
 * De verslagstatus van een afspraak (één regel voor board, relatie, portal-kaart):
 *  - AFGEZEGD                      -> NIET_NODIG (geen verslagplicht)
 *  - er is een verslag / AFGEROND  -> GEMAAKT
 *  - voorbij de vraag-drempel      -> TE_MAKEN (de kaart "actie vereist" staat)
 *  - anders                        -> NOG_NIET
 */
export function afspraakVerslagStatus(input: {
  status: PlanningAfspraakStatus
  verslagOp: Date | null
  datum: string | Date
  startTijd: string
  eindTijd: string
  nu: Date
  verslagVraagNaMinuten: number
}): AfspraakVerslagStatus {
  if (input.status === 'AFGEZEGD') return 'NIET_NODIG'
  if (input.verslagOp || input.status === 'AFGEROND') return 'GEMAAKT'
  const { eind } = afspraakInstanten(input)
  const vraagOp = eind.getTime() + input.verslagVraagNaMinuten * 60_000
  return input.nu.getTime() >= vraagOp ? 'TE_MAKEN' : 'NOG_NIET'
}

/** Uren tussen het einde van de afspraak en het verslag (doorlooptijd), afgerond op 1 decimaal. */
export function verslagDoorlooptijdUren(eind: Date, verslagOp: Date): number {
  const uren = (verslagOp.getTime() - eind.getTime()) / 3_600_000
  return Math.round(Math.max(0, uren) * 10) / 10
}

/** De NL-kalenderdag (`yyyy-mm-dd`) van een UTC-instant. */
export function afspraakNlDag(d: Date): string {
  return utcToAmsterdamWallclock(d).ymd
}

/**
 * Overlappen twee tijdvakken op dezelfde dag ("HH:mm", eind na start)? Halfopen:
 * aansluitende afspraken (10:00-11:00 en 11:00-12:00) overlappen niet.
 */
export function afspraakTijdenOverlappen(
  a: { startTijd: string; eindTijd: string },
  b: { startTijd: string; eindTijd: string },
): boolean {
  return a.startTijd < b.eindTijd && b.startTijd < a.eindTijd
}

// ─── Afscherming (spec 223 §C) ───────────────────────────────────────────────

/** De kijker zoals de afscherm-beslisser hem nodig heeft. */
export interface ContactMomentViewer {
  userId: string
  /** Medewerkerdossier van de kijker; null = account zonder dossier. */
  employeeId: string | null
  /** Hoort de kijker bij de doelgroep CRM-vertrouwelijk (rol, directie-vlag of persoon)? */
  crmVertrouwelijk: boolean
  /** Een ACCOUNTANT ziet nooit een afgeschermde inhoud (FO-404). */
  isAccountant?: boolean
  /**
   * Meekijken (impersonatie): de kijker is iemand anders dan wie er werkelijk
   * achter het scherm zit. Persoonsgebonden zicht (auteur, deelnemer,
   * accountmanager, doelgroep) erft hij niet: alleen openbare inhoud. Verplicht:
   * elke plek die een kijker bouwt, kiest (tsc bewaakt de keten).
   */
  meekijken: boolean
}

/** Het contactmoment zoals de beslisser het nodig heeft (geen inhoud). */
export interface ContactMomentZichtInput {
  zichtbaarheid: ContactMomentZichtbaarheid
  authorId: string
  verslagAuteurId: string | null
  /** De deelnemers van de gekoppelde afspraak (leeg zonder afspraak). */
  deelnemerEmployeeIds: readonly string[]
  /** De accountmanager(s) van de klant: eindverantwoordelijke en eerste contactpersoon. */
  accountmanagerEmployeeIds: readonly string[]
}

/**
 * Mag deze kijker de INHOUD van dit contactmoment zien (notitie, verslag,
 * bijlagen, tevredenheidsscore)? De ENIGE beslisser; de Prisma-where
 * `clientContactMomentWhere(viewer)` in de API is er de spiegel van. Het bestaan
 * van het contactmoment (datum, contactpersoon) is altijd zichtbaar voor wie het
 * CRM mag lezen: dat regelt de route, niet deze functie.
 */
export function magContactMomentInhoudZien(
  viewer: ContactMomentViewer,
  cm: ContactMomentZichtInput,
): boolean {
  if (cm.zichtbaarheid === 'OPENBAAR') return true
  if (viewer.isAccountant || viewer.meekijken) return false
  if (viewer.crmVertrouwelijk) return true
  if (cm.authorId === viewer.userId) return true
  if (cm.verslagAuteurId !== null && cm.verslagAuteurId === viewer.userId) return true
  if (viewer.employeeId === null) return false
  return (
    cm.deelnemerEmployeeIds.includes(viewer.employeeId) ||
    cm.accountmanagerEmployeeIds.includes(viewer.employeeId)
  )
}

/**
 * Hoort deze gebruiker bij de doelgroep CRM-vertrouwelijk? Rol, directie-vlag of
 * persoonlijk aangewezen. Nooit een ACCOUNTANT, ook niet als hij per vergissing
 * in de lijst staat.
 */
export function isCrmVertrouwelijk(
  gebruiker: { userId: string; role: string; isDirectie: boolean },
  instellingen: Pick<CrmAfspraakInstellingen, 'vertrouwelijkRollen' | 'vertrouwelijkDirectie' | 'vertrouwelijkUserIds'>,
): boolean {
  if (gebruiker.role === 'ACCOUNTANT') return false
  if ((instellingen.vertrouwelijkRollen as readonly string[]).includes(gebruiker.role)) return true
  if (instellingen.vertrouwelijkDirectie && gebruiker.isDirectie) return true
  return instellingen.vertrouwelijkUserIds.includes(gebruiker.userId)
}

// ─── Weergave (backoffice en portal) ─────────────────────────────────────────

/**
 * Rond een "HH:mm" af op het dichtstbijzijnde kwartier (de API accepteert alleen
 * :00/:15/:30/:45, net als de werkbon-tijden). Een onleesbare waarde blijft staan;
 * het schema meldt hem dan inline.
 */
export function afspraakTijdOpKwartier(tijd: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(tijd.trim())
  if (!m) return tijd
  const totaal = Number(m[1]) * 60 + Number(m[2])
  if (!Number.isFinite(totaal)) return tijd
  const afgerond = Math.min(23 * 60 + 45, Math.round(totaal / 15) * 15)
  return `${String(Math.floor(afgerond / 60)).padStart(2, '0')}:${String(afgerond % 60).padStart(2, '0')}`
}

export const AFSPRAAK_CONFLICT_LABELS: Record<PlanningConflictDTO['type'], string> = {
  DUBBELE_INPLANNING: 'Al ingepland',
  ZIEKTE: 'Ziek gemeld',
  TRAINING: 'Training of cursus',
  AFSPRAAK: 'Andere afspraak met een klant',
  VERLOF: 'Verlof',
}

const CONFLICT_TIJD_NL = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  hour: '2-digit',
  minute: '2-digit',
})

/**
 * Een conflict-tijd als "HH:mm". De meeste conflicten geven al "HH:mm"; een
 * training of cursus geeft een ISO-tijdstip, dat tonen we in NL-tijd.
 */
function conflictTijd(waarde: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T/.test(waarde)) return waarde
  const d = new Date(waarde)
  return Number.isNaN(d.getTime()) ? waarde : CONFLICT_TIJD_NL.format(d)
}

/** yyyy-mm-dd (of een ISO-string) -> dd-mm-jjjj. */
function conflictDatum(iso: string): string {
  const dag = iso.slice(0, 10)
  return `${dag.slice(8, 10)}-${dag.slice(5, 7)}-${dag.slice(0, 4)}`
}

/**
 * Eén leesbare regel per conflict, in de backoffice-dialogen en in de portal
 * ("Kasper: Al ingepland, werkbon W123 bij Agristo op 21-10-2030 07:00-16:00").
 */
export function afspraakConflictTekst(c: PlanningConflictDTO): string {
  const tijd =
    c.plannedStart && c.plannedEnd ? ` ${conflictTijd(c.plannedStart)}-${conflictTijd(c.plannedEnd)}` : ' (hele dag)'
  const label = AFSPRAAK_CONFLICT_LABELS[c.type] ?? 'Conflict'
  const wie = c.medewerkerNaam ? `${c.medewerkerNaam}: ` : ''
  if (c.type === 'DUBBELE_INPLANNING') {
    const bon = c.werkbonNummer ? `werkbon ${c.werkbonNummer}` : 'een dienst'
    const klant = c.klantNaam ? ` bij ${c.klantNaam}` : ''
    return `${wie}${label}, ${bon}${klant} op ${conflictDatum(c.datum)}${tijd}`
  }
  if (c.type === 'AFSPRAAK') {
    const klant = c.klantNaam ? ` met ${c.klantNaam}` : ''
    return `${wie}${label}${klant} op ${conflictDatum(c.datum)}${tijd}`
  }
  return `${wie}${label} op ${conflictDatum(c.datum)}${tijd}`
}
